import React, { useState, useEffect, useCallback } from 'react';
import {
    Box, Button, Typography, TextField, InputAdornment,
    Table, TableBody, TableCell, TableContainer, TableHead,
    TableRow, Paper, IconButton, Chip, CircularProgress,
    Tooltip, Select, MenuItem, FormControl, InputLabel
} from '@mui/material';
import {
    Add as AddIcon,
    Search as SearchIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    CheckCircle as ApproveIcon,
    Cancel as DeclineIcon,
    Payment as PaymentIcon,
    Visibility as ViewIcon,
    History as HistoryIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { orderListAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import NoPrivilege from '../../components/common/NoPrivilege';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import EnhancedPagination from '../../components/common/EnhancedPagination';
import OrderFormDialog from './OrderFormDialog';
import OrderDetailDialog from './OrderDetailDialog';
import PaymentDialog from './PaymentDialog';
import DeclineDialog from './DeclineDialog';
import PaymentHistoryDialog from './PaymentHistoryDialog';

// ── Status chip color map ─────────────────────────────────────────────────────
const STATUS_COLOR = {
    pending: 'warning',
    approved: 'success',
    declined: 'error',
};

const PAYMENT_STATUS_COLOR = {
    unpaid: 'error',
    partially_paid: 'warning',
    fully_paid: 'success',
};

const OrderList = () => {
    const { t } = useTranslation();
    const { user, isSuperAdmin } = useAuth();

    // ── Permissions ───────────────────────────────────────────────────────────
    const canView = isSuperAdmin || user?.privileges?.some(p => p.module === 'orderList' && p.operations.includes('view'));
    const canAdd = isSuperAdmin || user?.privileges?.some(p => p.module === 'orderList' && p.operations.includes('create'));
    const canEdit = isSuperAdmin || user?.privileges?.some(p => p.module === 'orderList' && p.operations.includes('update'));
    const canDelete = isSuperAdmin || user?.privileges?.some(p => p.module === 'orderList' && p.operations.includes('delete'));
    const canApprove = isSuperAdmin || user?.privileges?.some(p => p.module === 'orderList' && p.operations.includes('approve'));

    // ── State ─────────────────────────────────────────────────────────────────
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(false);
    const [privilegeError, setPrivilegeError] = useState(false);
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [paymentStatusFilter, setPaymentStatusFilter] = useState('');
    const [pagination, setPagination] = useState({
        page: 1, limit: 10, total: 0, pages: 1
    });

    // ── Dialog states ─────────────────────────────────────────────────────────
    const [formOpen, setFormOpen] = useState(false);
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [detailOpen, setDetailOpen] = useState(false);
    const [detailOrder, setDetailOrder] = useState(null);
    const [paymentOpen, setPaymentOpen] = useState(false);
    const [paymentOrder, setPaymentOrder] = useState(null);
    const [paymentHistoryOpen, setPaymentHistoryOpen] = useState(false);
    const [paymentHistoryOrder, setPaymentHistoryOrder] = useState(null);
    const [declineOpen, setDeclineOpen] = useState(false);
    const [declineOrder, setDeclineOrder] = useState(null);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [orderToDelete, setOrderToDelete] = useState(null);
    const [deleteLoading, setDeleteLoading] = useState(false);
    const [approveLoading, setApproveLoading] = useState(null);

    // ── Fetch ─────────────────────────────────────────────────────────────────
    const fetchOrders = useCallback(async () => {
        setLoading(true);
        setPrivilegeError(false);
        try {
            const params = {
                page: pagination.page,
                limit: pagination.limit,
                sortBy: 'createdAt',
                sortOrder: 'desc',
            };
            if (search.trim()) params.search = search.trim();
            if (statusFilter) params.status = statusFilter;
            if (paymentStatusFilter) params.paymentStatus = paymentStatusFilter;

            const res = await orderListAPI.getAll(params);
            setOrders(res.data?.data || []);
            setPagination(prev => ({
                ...prev,
                total: res.data?.pagination?.total ?? 0,
                pages: res.data?.pagination?.pages ?? 1,
            }));
        } catch (err) {
            if (err.response?.status === 403) {
                setPrivilegeError(true);
            } else {
                toast.error(t('orderList.fetchError'));
            }
        } finally {
            setLoading(false);
        }
    }, [pagination.page, pagination.limit, search, statusFilter, paymentStatusFilter, t]);

    useEffect(() => { fetchOrders(); }, [fetchOrders]);

    useEffect(() => {
        setPagination(prev => ({ ...prev, page: 1 }));
    }, [search, statusFilter, paymentStatusFilter]);

    // ── Action Handlers ───────────────────────────────────────────────────────
    const handleApprove = async (order) => {
        setApproveLoading(order._id);
        try {
            await orderListAPI.approve(order._id);
            toast.success(t('orderList.approveSuccess'));
            fetchOrders();
        } catch (err) {
            toast.error(err.response?.data?.message || t('orderList.approveError'));
        } finally {
            setApproveLoading(null);
        }
    };

    const handleDeclineSuccess = () => {
        setDeclineOpen(false);
        setDeclineOrder(null);
        fetchOrders();
    };

    const handlePaymentSuccess = () => {
        setPaymentOpen(false);
        setPaymentOrder(null);
        fetchOrders();
    };

    const handleDeleteConfirm = async () => {
        if (!orderToDelete) return;
        setDeleteLoading(true);
        try {
            await orderListAPI.delete(orderToDelete._id);
            toast.success(t('orderList.deleteSuccess'));
            setDeleteDialogOpen(false);
            setOrderToDelete(null);
            fetchOrders();
        } catch (err) {
            toast.error(err.response?.data?.message || t('orderList.deleteError'));
        } finally {
            setDeleteLoading(false);
        }
    };

    // ── Privilege gate ────────────────────────────────────────────────────────
    if (!canView || privilegeError) {
        return (
            <NoPrivilege
                message={t('orderList.noPrivilegeError', 'You do not have permission to view orders.')}
            />
        );
    }

    // ── Render ────────────────────────────────────────────────────────────────
    return (
        <Box>

            {/* ── Header ── */}
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <Typography variant="h5" fontWeight="bold">
                    {t('orderList.title')}
                </Typography>
                {canAdd && (
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => { setSelectedOrder(null); setFormOpen(true); }}
                    >
                        {t('orderList.addButton')}
                    </Button>
                )}
            </Box>

            {/* ── Filters ── */}
            <Box display="flex" gap={2} mb={2} flexWrap="wrap">
                <TextField
                    size="small"
                    placeholder={t('orderList.searchPlaceholder')}
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon fontSize="small" />
                            </InputAdornment>
                        ),
                    }}
                    sx={{ width: 280 }}
                />
                <FormControl size="small" sx={{ width: 180 }}>
                    <InputLabel>{t('orderList.status')}</InputLabel>
                    <Select
                        value={statusFilter}
                        onChange={e => setStatusFilter(e.target.value)}
                        label={t('orderList.status')}
                    >
                        <MenuItem value="">{t('orderList.allStatuses')}</MenuItem>
                        <MenuItem value="pending">{t('orderList.statusPending')}</MenuItem>
                        <MenuItem value="approved">{t('orderList.statusApproved')}</MenuItem>
                        <MenuItem value="declined">{t('orderList.statusDeclined')}</MenuItem>
                    </Select>
                </FormControl>
                <FormControl size="small" sx={{ width: 200 }}>
                    <InputLabel>{t('orderList.paymentStatusLabel', 'Payment Status')}</InputLabel>
                    <Select
                        value={paymentStatusFilter}
                        onChange={e => setPaymentStatusFilter(e.target.value)}
                        label={t('orderList.paymentStatusLabel', 'Payment Status')}
                    >
                        <MenuItem value="">{t('common.all', 'All')}</MenuItem>
                        <MenuItem value="unpaid">{t('orderList.paymentStatus.unpaid', 'Unpaid')}</MenuItem>
                        <MenuItem value="partially_paid">{t('orderList.paymentStatus.partially_paid', 'Partially Paid')}</MenuItem>
                        <MenuItem value="fully_paid">{t('orderList.paymentStatus.fully_paid', 'Fully Paid')}</MenuItem>
                    </Select>
                </FormControl>
            </Box>

            {/* ── Table ── */}
            <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell><strong>{t('orderList.orderNumber')}</strong></TableCell>
                            <TableCell><strong>{t('orderList.supplier')}</strong></TableCell>
                            <TableCell><strong>{t('orderList.totalPrice')}</strong></TableCell>
                            <TableCell><strong>{t('orderList.paidPrice')}</strong></TableCell>
                            <TableCell><strong>{t('orderList.remainingBalance')}</strong></TableCell>
                            <TableCell><strong>{t('orderList.status')}</strong></TableCell>
                            <TableCell><strong>{t('orderList.paymentStatusLabel', 'Payment')}</strong></TableCell>
                            <TableCell><strong>{t('common.createdAt')}</strong></TableCell>
                            <TableCell align="center"><strong>{t('common.actions')}</strong></TableCell>
                        </TableRow>
                    </TableHead>

                    <TableBody>
                        {loading ? (
                            <TableRow>
                                <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                                    <CircularProgress size={32} />
                                </TableCell>
                            </TableRow>
                        ) : orders.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                                    <Typography color="text.secondary">
                                        {t('orderList.noItemsFound')}
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        ) : orders.map(order => (
                            <TableRow key={order._id} hover>

                                <TableCell>
                                    <Typography fontWeight={500} variant="body2">
                                        {order.orderNumber}
                                    </Typography>
                                </TableCell>

                                <TableCell>{order.supplier?.name || '—'}</TableCell>

                                <TableCell>
                                    {order.totalPrice?.toLocaleString()} {t('common.currency')}
                                </TableCell>

                                <TableCell>
                                    {order.paidPrice?.toLocaleString()} {t('common.currency')}
                                </TableCell>

                                <TableCell>
                                    <Typography
                                        color={order.remainingBalance > 0 ? 'error.main' : 'success.main'}
                                        variant="body2"
                                        fontWeight={500}
                                    >
                                        {order.remainingBalance?.toLocaleString()} {t('common.currency')}
                                    </Typography>
                                </TableCell>

                                <TableCell>
                                    <Chip
                                        label={t(`orderList.status${order.status.charAt(0).toUpperCase() + order.status.slice(1)}`)}
                                        color={STATUS_COLOR[order.status] || 'default'}
                                        size="small"
                                    />
                                </TableCell>

                                <TableCell>
                                    {order.paymentStatus && (
                                        <Chip
                                            label={t(
                                                `orderList.paymentStatus.${order.paymentStatus}`,
                                                order.paymentStatus.replace('_', ' ')
                                            )}
                                            color={PAYMENT_STATUS_COLOR[order.paymentStatus] || 'default'}
                                            size="small"
                                            variant="outlined"
                                        />
                                    )}
                                </TableCell>

                                <TableCell>
                                    {order.createdAt
                                        ? new Date(order.createdAt).toLocaleDateString()
                                        : '—'}
                                </TableCell>

                                <TableCell align="center">

                                    {/* View */}
                                    <Tooltip title={t('common.view')}>
                                        <IconButton
                                            size="small"
                                            onClick={() => { setDetailOrder(order); setDetailOpen(true); }}
                                        >
                                            <ViewIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>

                                    {/* Payment History — show if order has any payments */}
                                    {order.paidPrice > 0 && (
                                        <Tooltip title={t('orderList.paymentHistory.title', 'Payment History')}>
                                            <IconButton
                                                size="small"
                                                color="info"
                                                onClick={() => {
                                                    setPaymentHistoryOrder(order);
                                                    setPaymentHistoryOpen(true);
                                                }}
                                            >
                                                <HistoryIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    )}

                                    {/* Edit — pending + canEdit */}
                                    {canEdit && order.status === 'pending' && (
                                        <Tooltip title={t('common.edit')}>
                                            <IconButton
                                                size="small"
                                                onClick={() => { setSelectedOrder(order); setFormOpen(true); }}
                                            >
                                                <EditIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    )}

                                    {/* Approve — pending + canApprove */}
                                    {canApprove && order.status === 'pending' && (
                                        <Tooltip title={t('orderList.approve')}>
                                            <IconButton
                                                size="small"
                                                color="success"
                                                onClick={() => handleApprove(order)}
                                                disabled={approveLoading === order._id}
                                            >
                                                {approveLoading === order._id
                                                    ? <CircularProgress size={16} />
                                                    : <ApproveIcon fontSize="small" />
                                                }
                                            </IconButton>
                                        </Tooltip>
                                    )}

                                    {/* Decline — pending + canApprove */}
                                    {canApprove && order.status === 'pending' && (
                                        <Tooltip title={t('orderList.decline')}>
                                            <IconButton
                                                size="small"
                                                color="error"
                                                onClick={() => { setDeclineOrder(order); setDeclineOpen(true); }}
                                            >
                                                <DeclineIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    )}

                                    {/* Add Payment — not declined + canEdit + balance > 0 */}
                                    {canEdit && order.status !== 'declined' && order.remainingBalance > 0 && (
                                        <Tooltip title={t('orderList.addPayment')}>
                                            <IconButton
                                                size="small"
                                                color="primary"
                                                onClick={() => { setPaymentOrder(order); setPaymentOpen(true); }}
                                            >
                                                <PaymentIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    )}

                                    {/* Delete — not approved + canDelete */}
                                    {canDelete && order.status !== 'approved' && (
                                        <Tooltip title={t('common.delete')}>
                                            <IconButton
                                                size="small"
                                                color="error"
                                                onClick={() => { setOrderToDelete(order); setDeleteDialogOpen(true); }}
                                            >
                                                <DeleteIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    )}

                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* ── Pagination ── */}
            {!loading && orders.length > 0 && (
                <Box mt={2}>
                    <EnhancedPagination
                        pagination={pagination}
                        onPageChange={p => setPagination(prev => ({ ...prev, page: p }))}
                        onLimitChange={l => setPagination(prev => ({ ...prev, limit: l, page: 1 }))}
                    />
                </Box>
            )}

            {/* ── Dialogs ── */}
            {canAdd && (
                <OrderFormDialog
                    open={formOpen}
                    onClose={() => { setFormOpen(false); setSelectedOrder(null); }}
                    onSuccess={() => { setFormOpen(false); setSelectedOrder(null); fetchOrders(); }}
                    order={selectedOrder}
                />
            )}

            <OrderDetailDialog
                open={detailOpen}
                onClose={() => { setDetailOpen(false); setDetailOrder(null); }}
                order={detailOrder}
            />

            {canEdit && (
                <PaymentDialog
                    open={paymentOpen}
                    onClose={() => { setPaymentOpen(false); setPaymentOrder(null); }}
                    onSuccess={handlePaymentSuccess}
                    order={paymentOrder}
                />
            )}

            <PaymentHistoryDialog
                open={paymentHistoryOpen}
                onClose={() => { setPaymentHistoryOpen(false); setPaymentHistoryOrder(null); }}
                order={paymentHistoryOrder}
            />

            {canApprove && (
                <DeclineDialog
                    open={declineOpen}
                    onClose={() => { setDeclineOpen(false); setDeclineOrder(null); }}
                    onSuccess={handleDeclineSuccess}
                    order={declineOrder}
                />
            )}

            {canDelete && (
                <ConfirmDialog
                    open={deleteDialogOpen}
                    title={t('orderList.deleteConfirmTitle')}
                    message={t('orderList.deleteConfirmMessage')}
                    onConfirm={handleDeleteConfirm}
                    onCancel={() => { setDeleteDialogOpen(false); setOrderToDelete(null); }}
                    loading={deleteLoading}
                />
            )}

        </Box>
    );
};

export default OrderList;